import {FeedMonitor} from '../components/FeedMonitor';
import { useState, useEffect } from 'react';
import { LIVE_SOLUTIONS, findSolution } from '../../../api/src/solutions/solution-catalog';
import { Link, useSearchParams } from '../lib/localized-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { CreateLiveStreamWizard } from '../components/CreateLiveStreamWizard';
import { EventArtwork } from '../components/EventArtwork';
import { useT } from '../lib/i18n';
import { Alert, Modal, EmptyState, StatusBadge } from '../components/ui';
import { PlusIcon, StreamIcon, ScenariosIcon, ListIcon } from '../components/icons';

const STREAM_VIEW_KEY = 'kardolive-streams-view';

export function StreamsPage() {
  const { t, locale } = useT();
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const solutionName = (id?: string) => findSolution(id) ? t('streamType.' + id) : t('streams.custom');
  const [params, setParams] = useSearchParams();
  const showModal = ['1', 'blank'].includes(params.get('create') ?? '');
  const qc = useQueryClient();
  const monitoring = params.get('view') === 'monitor';
  const archivedView = params.get('archived') === '1';
  const [tidying, setTidying] = useState(false);
  const [search, setSearch] = useState('');
  const [solution, setSolution] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('');
  const [view, setView] = useState<'grid' | 'list'>(() => {
    try { return localStorage.getItem(STREAM_VIEW_KEY) === 'list' ? 'list' : 'grid'; }
    catch { return 'grid'; }
  });
  useEffect(() => {
    try { localStorage.setItem(STREAM_VIEW_KEY, view); }
    catch { /* The toggle still works when browser storage is unavailable. */ }
  }, [view]);
  const collectionClass = `stream-grid stream-collection${view === 'list' ? ' stream-list' : ''}`;
  const list = useQuery({
    queryKey: ['my-streams', archivedView ? 'archived' : 'active'],
    enabled: !monitoring,
    queryFn: async () => (await api.get('/me/streams', { params: archivedView ? { archived: 1 } : {} })).data,
    refetchInterval: archivedView ? false : 5_000,
  });
  const filtered = (list.data ?? []).filter((s: any) =>
    s.name.toLocaleLowerCase(locale).includes(search.trim().toLocaleLowerCase(locale)) &&
    (!solution || (solution === 'custom' ? !findSolution(s.metadata?.solution?.id) : s.metadata?.solution?.id === solution)) &&
    (!status || s.status === status) && (!days || new Date(s.createdAt).getTime() >= Date.now() - Number(days) * 86400000));
  const restore = useMutation({
    mutationFn: async (id: string) => (await api.post(`/me/streams/${id}/restore`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-streams'] }),
  });
  // Streams made and never used — the litter a test drive leaves behind.
  // A day's grace so something created this morning isn't called unused.
  const unused: any[] = archivedView ? [] : (list.data ?? []).filter((s: any) =>
    s.status !== 'LIVE' && s._count.sessions === 0 && s._count.recordings === 0 &&
    Date.now() - new Date(s.createdAt).getTime() > 24 * 3600_000);

  return (
    <div>
      <div className="row-between stream-page-header" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{t('nav.streams')}</h1>
          <div className="tabs" style={{ marginTop: 10, marginBottom: 0 }}>
            <button className={`tab ${archivedView || monitoring ? '' : 'active'}`} onClick={() => setParams({})}>{t('streams.active')}</button>
            <button className={`tab ${archivedView ? 'active' : ''}`} onClick={() => setParams({ archived: '1' })}>{t('streams.archived')}</button>
            <button className={`tab ${monitoring ? 'active' : ''}`} onClick={() => setParams({ view: 'monitor' })}>{t('streams.monitor')}</button>
          </div>
        </div>
        <div className="stream-list-actions">
          {!monitoring&&<div className="stream-view-toggle" role="group" aria-label={t('streams.view')}>
            <button type="button" className="btn ghost sm" aria-pressed={view === 'list'} onClick={() => setView('list')}>
              <ListIcon/> {t('streams.list')}
            </button>
            <button type="button" className="btn ghost sm" aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
              <ScenariosIcon/> {t('streams.grid')}
            </button>
          </div>}
          <button className="btn lg" onClick={() => setParams(previous => { const next = new URLSearchParams(previous); next.set('create', '1'); return next; })}><PlusIcon size={16}/> {t('streams.create')}</button>
        </div>
      </div>

      {monitoring ? <FeedMonitor/> : <>
      <div className="flex gap stream-filters" style={{ flexWrap: 'wrap', marginBottom: 16 }}>
        <input className="input" style={{ flex: '1 1 180px', width: 'auto' }} aria-label={t('streams.searchLabel')} placeholder={t('streams.searchHint')} value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ flex: '1 1 180px', width: 'auto' }} aria-label={t('streams.solutionFilter')} value={solution} onChange={e => setSolution(e.target.value)}><option value="">{t('streams.allSolutions')}</option>{LIVE_SOLUTIONS.map(s => <option key={s.id} value={s.id}>{solutionName(s.id)}</option>)}<option value="custom">{t('streams.custom')}</option></select>
        <select className="input" style={{ flex: '0 1 170px', width: 'auto' }} aria-label={t('streams.statusFilter')} value={status} onChange={e => setStatus(e.target.value)}><option value="">{t('streams.allStatuses')}</option>{['LIVE', 'IDLE', 'ENDED', 'ARCHIVED'].map(s => <option key={s} value={s}>{t('status.' + s.toLowerCase())}</option>)}</select>
        <select className="input" style={{ flex: '0 1 180px', width: 'auto' }} aria-label={t('streams.dateFilter')} value={days} onChange={e => setDays(e.target.value)}><option value="">{t('streams.anyTime')}</option><option value="7">{t('streams.last7')}</option><option value="30">{t('streams.last30')}</option></select>
      </div>
      {list.data?.length > 0 && <p className="muted" role="status">{t('streams.count', { shown: number(filtered.length), total: number(list.data.length) })}</p>}
      {list.data?.length > 0 && filtered.length === 0 && <div className="card"><p>{t('streams.noMatch')}</p><button className="btn outline" onClick={() => { setSearch(''); setSolution(''); setStatus(''); setDays(''); }}>{t('streams.clearFilters')}</button></div>}
      {list.isError && (
        <Alert title={t(list.data ? 'streams.refreshFailed' : 'streams.loadFailed')}>
          <p>{t(list.data ? 'streams.stale' : 'streams.connectionHint')}</p>
          <button className="btn outline sm" disabled={list.isFetching} onClick={() => void list.refetch()}>
            {t(list.isFetching ? 'streams.retrying' : 'streams.retry')}
          </button>
        </Alert>
      )}
      {restore.isError && (
        <Alert title={t('streams.restoreFailed')}>
          {t('streams.restoreHint')}
        </Alert>
      )}

      {unused.length > 0 && (
        <div className="card row-between" style={{ marginBottom: 16, gap: 12 }}>
          <div>
            <strong>{t('streams.unused', { count: number(unused.length) })}</strong>
            <div className="muted" style={{ fontSize: 13 }}>{t('streams.unusedHint')}</div>
          </div>
          <button className="btn outline" onClick={() => setTidying(true)}>{t('streams.reviewArchive')}</button>
        </div>
      )}

      {list.isPending ? (
        <div className="card muted" role="status" style={{ padding: 28 }}>{t('streams.loading')}</div>
      ) : !list.data ? null : archivedView ? (
        list.data && list.data.length === 0 ? (
          <div className="card muted" style={{ textAlign: 'center', padding: 28 }}>{t('streams.archiveEmpty')}</div>
        ) : (
          <div className={collectionClass}>
            {filtered.map((s: any) => (
              <div key={s.id} className="stream-card">
                <EventArtwork stream={s} compact={view === 'list'} />
                <div className="head">
                  <div>
                    <Link to={`/streams/${s.id}`} className="name">{s.name}</Link>
                    <div className="meta" style={{ marginTop: 4 }}>
                      <span>{t('streams.broadcasts', { count: number(s._count.sessions) })}</span><span>·</span><span>{t('streams.replays', { count: number(s._count.recordings) })}</span>
                    </div>
                  </div>
                  <button className="btn sm outline" disabled={restore.isPending} onClick={() => restore.mutate(s.id)}>{t('streams.restore')}</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : list.data && list.data.length === 0 ? (
        <EmptyState
          icon={<StreamIcon/>}
          title={t('streams.firstTitle')}
          body={t('streams.firstBody')}
          action={<div className="flex gap" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link className="btn lg" to="/streams?create=1">{t('streams.create')}</Link>
            <button className="btn outline lg" onClick={() => setParams({ create: 'blank' })}><PlusIcon size={16}/> {t('streams.blank')}</button>
          </div>}
        />
      ) : (
        <div className={collectionClass}>
          {filtered.map((s: any) => (
            <Link key={s.id} to={`/streams/${s.id}?tab=prepare`} className="stream-card">
              <EventArtwork stream={s} compact={view === 'list'} />
              <div className="head">
                <div>
                  <div className="name">{s.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{solutionName(s.metadata?.solution?.id)}</div>
                  <div className="meta" style={{ marginTop: 4 }}>
                    <span>{['BROADCAST','LOW_LATENCY','REALTIME'].includes(s.mode) ? t('streams.mode.' + s.mode) : s.mode}</span>
                    <span>·</span>
                    <span>{t('streams.sessions', { count: number(s._count.sessions) })}</span>
                    <span>·</span>
                    <span>{t('streams.recordings', { count: number(s._count.recordings) })}</span>
                  </div>
                </div>
                <StatusBadge status={s.status}/>
              </div>
              <div className="meta" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'var(--text-mute)' }}>
                {t('streams.key')}: <bdi dir="ltr">{s.keys[0]?.key.slice(0, 14)}…</bdi>
              </div>
            </Link>
          ))}
        </div>
      )}

      {tidying && <TidyModal streams={unused} onClose={() => setTidying(false)} onDone={() => { setTidying(false); qc.invalidateQueries({ queryKey: ['my-streams'] }); }} />}
      </>}
      {showModal && <CreateLiveStreamWizard startBlank={params.get('create') === 'blank'} onClose={() => setParams(previous => { const next = new URLSearchParams(previous); next.delete('create'); return next; }, { replace: true })} />}
    </div>
  );
}

/** Archive several never-used streams at once. Everything starts ticked;
 *  untick anything worth keeping. */
function TidyModal({ streams, onClose, onDone }: { streams: any[]; onClose: () => void; onDone: () => void }) {
  const { t, locale } = useT();
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(streams.map((s) => s.id)));
  const [failed, setFailed] = useState(0);
  const run = useMutation({
    mutationFn: async () => {
      let bad = 0;
      for (const id of picked) {
        try { await api.post(`/me/streams/${id}/archive`); } catch { bad++; }
      }
      return bad;
    },
    onSuccess: (bad) => { if (bad) setFailed(bad); else onDone(); },
  });
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0 }}>{t('streams.archiveTitle')}</h2>
      <p className="modal-subtitle">{t('streams.archiveHint')}</p>
      <div className="tidy-list">
        {streams.map((s) => (
          <label key={s.id} className="tidy-row">
            <input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)} />
            <span className="tidy-name">{s.name}</span>
            <span className="muted" style={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString(locale)}</span>
          </label>
        ))}
      </div>
      {failed > 0 && <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 10 }}>{t('streams.archiveFailed', { count: number(failed) })}</div>}
      <div className="flex gap" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
        <button className="btn outline" onClick={failed ? onDone : onClose}>{t(failed ? 'streams.close' : 'common.cancel')}</button>
        {!failed && (
          <button className="btn" disabled={picked.size === 0 || run.isPending} onClick={() => run.mutate()}>
            {t(run.isPending ? 'streams.archiving' : 'streams.archiveCount', { count: number(picked.size) })}
          </button>
        )}
      </div>
    </Modal>
  );
}
